// src/services/dashboardApi.ts
//
// Single source of truth for dashboard-authenticated backend calls.
// Replaces four independent copies of getDashboardToken/authedFetch
// (useDashboardChat.ts, DashboardShell.tsx, DashboardOutcomes.tsx,
// DashboardOutcomeDetail.tsx) that each had their own module-scope
// token cache — meaning none of them actually shared state with each
// other. This module is imported once; its cache is shared everywhere.
//
// Also bakes in the /api prefix for all data routes (see the /dashboard
// route/rewrite collision writeup — outcomes/**, audit — this is the
// fix). Non-data routes (session, chat, tools) are NOT under /api and
// are called via authedFetchRaw, which takes a full /dashboard/... path.

import { auth } from './firebase';

const FDW_BASE = 'https://fivedollarworkday.com';

let dashboardToken: string | null = null;

async function getDashboardToken(): Promise<string> {
  if (dashboardToken) return dashboardToken;
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in.');
  const res = await fetch(`${FDW_BASE}/dashboard/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) throw new Error('Failed to establish dashboard session.');
  const data = await res.json();
  dashboardToken = data.access_token;
  return dashboardToken;
}

// For data routes under /dashboard/api/* — path is relative, e.g.
// authedFetch('/outcomes') resolves to /dashboard/api/outcomes.
export async function authedFetch(path: string, options: RequestInit = {}) {
  const token = await getDashboardToken();
  return fetch(`${FDW_BASE}/dashboard/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}

// For non-/api routes that still need the same token — chat, tools.
// Takes a full path starting with /dashboard/...
export async function authedFetchRaw(path: string, options: RequestInit = {}) {
  const token = await getDashboardToken();
  return fetch(`${FDW_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
}